import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useRef, useState } from 'react';

import BlankState from '@fmbt/ui/BlankState';
import { LoadingLogo } from '@fmbt/ui/LoadingSpinner';
import DefaultButton from 'components/atoms/DefaultButton';
import AlertIcon from 'components/icons/AlertIcon';
import { isNativeContainer } from 'utils/native-container';

/**
 * How long to wait for `loginWithRedirect`'s navigation to actually land
 * before assuming Android's WebView silently dropped it and retrying. Well
 * above the ~1.5s a successful redirect takes in practice (confirmed via
 * logcat), so a slow network doesn't trigger a spurious retry.
 */
const REDIRECT_WATCHDOG_MS = 3000;

/** One retry only: if the WebView drops the navigation twice in a row,
 * something else is wrong and looping would just hide that. */
const MAX_REDIRECT_ATTEMPTS = 2;

/** Auth0's `AuthorizationParams` has no typed slot for this, so the same
 * literal is used to send it to loginWithRedirect and to read it off the URL
 * — kept as one constant rather than two copies that could drift apart. */
const SESSION_TRANSFER_TOKEN_PARAM = 'session_transfer_token';

/**
 * Captures the Auth0 Native-to-Web SSO session transfer token from the URL
 * and scrubs it immediately, at module-load time, before analytics, the
 * router or browser history can observe it (mobile app's own
 * src/services/sessionTransfer.ts appends it). Single-use and ~60s-lived.
 *
 * Residual risk: a URL-delivered token is still a login-CSRF / session-
 * fixation window — a victim opening an attacker's link within that short
 * life could sign into the attacker's account. Scrubbing immediately and
 * relying on single-use/short-life is the pragmatic mitigation; closing it
 * fully needs a native-generated nonce verified server-side (follow-up).
 */
function captureAndScrubSessionTransferToken(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get(SESSION_TRANSFER_TOKEN_PARAM);
    if (token === null) return null;

    params.delete(SESSION_TRANSFER_TOKEN_PARAM);
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname +
        (query ? `?${query}` : '') +
        window.location.hash
    );
    return token;
  } catch {
    return null;
  }
}

const sessionTransferToken = captureAndScrubSessionTransferToken();
// DEBUG(temp): diagnosing a stuck "inside app auth" screen on Android dev,
// remove once resolved. JSON.stringify because logcat shows plain object
// args as "[object Object]".
// eslint-disable-next-line no-console
console.log('[SessionTransferGate DEBUG] module load ' + JSON.stringify({
  hasToken: !!sessionTransferToken,
  isNative: isNativeContainer(),
  url: window.location.href
}));

// DEBUG(temp): a plain heartbeat, independent of any gate state, to tell
// whether timers (and JS generally) keep running at all after a stuck
// redirect, or whether the whole page is suspended — the previous watchdog
// fix didn't fire even once in two separate real-device repros, so this
// isolates whether that's a setTimeout-specific bug or something stopping
// JS execution outright.
let heartbeatCount = 0;
window.setInterval(() => {
  heartbeatCount += 1;
  // eslint-disable-next-line no-console
  console.log('[SessionTransferGate DEBUG] heartbeat ' + heartbeatCount + ' url=' + window.location.href + ' visibility=' + document.visibilityState);
}, 1000);

interface Props {
  children: JSX.Element;
}

/**
 * Redeems an Auth0 Native-to-Web SSO session transfer token through a single
 * explicit login, and blocks `children` (routes, their API calls, the axios
 * 401 interceptor) until that resolves — so the only thing that can call
 * Auth0 while a token is pending is this one explicit login, winning the
 * redemption race by construction and keeping the token off the provider's
 * default authorizationParams (so it never rides a silent/iframe/refresh
 * call). Only honours a token from inside the native container; a phishing
 * link opened in an ordinary browser is ignored.
 *
 * After loginWithRedirect returns, it's a fresh page load whose URL no
 * longer carries the token (scrubbed on first read), so this gate is
 * transparent from then on — including when redemption failed, which falls
 * through to the ordinary login flow rather than getting stuck.
 *
 * No-op in an ordinary browser (no token present).
 *
 * **Android watchdog:** `loginWithRedirect`'s promise resolving only means
 * the SDK issued the navigation, not that Android's WebView actually
 * performed it. Confirmed via logcat on a real device: roughly 1 in 3
 * attempts, the WebView silently drops a JS-triggered navigation issued
 * shortly after the app resumes from the system browser (no error, no
 * `Linking`/`Intent` activity — the page just never changes), leaving this
 * gate stuck on `LoadingLogo` forever. `attemptCount` retries once if the
 * gate is still showing after `REDIRECT_WATCHDOG_MS`.
 */
const SessionTransferGate = ({ children }: Props): JSX.Element => {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();
  const inFlightRef = useRef(false);
  // Deliberately NOT cleared from this effect's cleanup function. In dev,
  // StrictMode double-invokes effects (mount → simulated unmount → mount)
  // to surface missing-cleanup bugs, and an effect-cleanup-based
  // `clearTimeout` here would cancel the watchdog a few milliseconds after
  // arming it on every single run, long before it could ever fire — this
  // was caught live: the retry never happened on a real stuck repro, with
  // no "watchdog fired" log at all despite waiting 30+ seconds. The
  // watchdog's own correctness doesn't depend on component unmount order,
  // so it's armed and disarmed explicitly against this ref instead.
  const watchdogTimeoutRef = useRef<number | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);

  const hasNativeSessionTransferToken = !!sessionTransferToken && isNativeContainer();

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[SessionTransferGate DEBUG] effect run ' + JSON.stringify({
      hasNativeSessionTransferToken,
      isLoading,
      isAuthenticated,
      inFlight: inFlightRef.current,
      attemptCount,
      url: window.location.href
    }));
    if (
      !hasNativeSessionTransferToken ||
      isLoading ||
      isAuthenticated ||
      inFlightRef.current ||
      attemptCount >= MAX_REDIRECT_ATTEMPTS
    ) {
      return;
    }
    inFlightRef.current = true;
    // eslint-disable-next-line no-console
    console.log('[SessionTransferGate DEBUG] calling loginWithRedirect, attempt=' + (attemptCount + 1) + ' url=' + window.location.href);
    loginWithRedirect({
      authorizationParams: { [SESSION_TRANSFER_TOKEN_PARAM]: sessionTransferToken }
    }).then(() => {
      // eslint-disable-next-line no-console
      console.log('[SessionTransferGate DEBUG] loginWithRedirect resolved, url=' + window.location.href);
    }).catch(error => {
      // eslint-disable-next-line no-console
      console.log('[SessionTransferGate DEBUG] loginWithRedirect rejected: ' + String(error) + ' url=' + window.location.href);
    });

    // If the WebView actually navigates, this component (and its whole JS
    // context) is torn down before this fires — on module reload the gate
    // starts over with no token (already scrubbed) and renders `children`
    // straight away. So a firing timer unambiguously means the navigation
    // was dropped, not that it's merely slow.
    if (watchdogTimeoutRef.current !== null) {
      window.clearTimeout(watchdogTimeoutRef.current);
    }
    watchdogTimeoutRef.current = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log('[SessionTransferGate DEBUG] watchdog fired, navigation appears dropped, url=' + window.location.href);
      watchdogTimeoutRef.current = null;
      inFlightRef.current = false;
      setAttemptCount(count => count + 1);
    }, REDIRECT_WATCHDOG_MS);
  }, [hasNativeSessionTransferToken, isLoading, isAuthenticated, loginWithRedirect, attemptCount]);

  if (hasNativeSessionTransferToken && !isAuthenticated) {
    // Both attempts dropped — retrying again would just loop silently, so
    // give the user an explicit way out rather than leaving this spinning
    // forever (the stuck state this watchdog exists to catch in the first
    // place).
    if (attemptCount >= MAX_REDIRECT_ATTEMPTS) {
      return (
        <div style={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          marginLeft: 24,
          marginRight: 24,
          marginTop: 80
        }}>
          <BlankState
            Icon={AlertIcon}
            title="Something went wrong"
            subTitle="We couldn't finish signing you in. Please try again."
          />
          <DefaultButton type="primary" onClick={() => window.location.reload()}>
            Retry
          </DefaultButton>
        </div>
      );
    }
    return <LoadingLogo />;
  }

  return children;
};

export default SessionTransferGate;
